/**
 * External Imports
 */

import { connect, useDispatch } from 'react-redux';
import { FC, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
/**
 * Nexus Imports
 */
import { NeuIcon } from '@neutron/react';
import { useAnalyticsApi } from '@shared-web-analytics/react';
/**
 * Internal Imports
 */
import TransitionalBarChart from '../../metricsChart/barChart/TransitionalBarChart';
import { RootState } from '../../../redux/store';
import {
  setDelegatedEmployee,
  setFeedbackFilter
} from '../../../redux/actions';
import { logReportsClick, ReportEvents } from '../../../utils/analyticsHelpers';
/**
 * Global Type Definition Imports
 */
import {
  ApiMetrics,
  Filter,
  IDelegateOption,
  MetricsData
} from '../../../config/interfaces';
/**
 * Style Imports
 */
import {
  HeaderContainer,
  TaskMetricContainer,
  TitleContainer
} from '../CustomMetricCharts.styles';

interface EmpCoachingMetricChartProps {
  metrics: ApiMetrics;
  employeeSummaryReportFilter: Filter;
  selectedDelegate: IDelegateOption;
}

const EmpCoachingMetricChart: FC<EmpCoachingMetricChartProps> = ({
  metrics,
  employeeSummaryReportFilter,
  selectedDelegate
}) => {
  const dispatch = useDispatch();
  const navigateTo = useNavigate();
  const { '*': view } = useParams();
  const { logTrackingEvent } = useAnalyticsApi();

  const metricsData: MetricsData = useMemo(() => {
    return {
      totalRecordsCount: metrics.totalRecordsCount,
      metrics: [
        {
          label: 'Opened',
          value: metrics.openedRecordsCount,
          color: '#df5828'
        },
        {
          label: 'Completed',
          value: metrics.completedRecordsCount,
          color: '#0077b5'
        }
      ]
    };
  }, [metrics]);

  const openFeedbackTable = () => {
    dispatch(
      setFeedbackFilter({
        ...employeeSummaryReportFilter,
        startDate:
          employeeSummaryReportFilter.startDate ??
          format(
            new Date(new Date().getFullYear(), new Date().getMonth(), 1),
            'yyyy-MM-dd'
          ),
        category: '',
        subCategory: '',
        createdBy: '',
        status: '',
        taskSubType: 'coaching',
        employeeId: ''
      })
    );
    dispatch(setDelegatedEmployee(selectedDelegate));
    if (view)
      logTrackingEvent(
        logReportsClick(view?.replace('/', '-'), 'coaching' as ReportEvents)
      );
    setTimeout(() => {
      navigateTo('/employees/feedback', {
        state: { from: view }
      });
    }, 1000);
  };

  return (
    <TaskMetricContainer
      data-testid="employee-coaching-metric-chart"
      onClick={openFeedbackTable}
    >
      <HeaderContainer style={{ paddingLeft: '3.5em' }}>
        <NeuIcon size="1x" color="blue" className="ms-1">
          flag
        </NeuIcon>
        <TitleContainer
          data-testid="employee-coaching-metric-label"
          style={{ marginLeft: '0.5rem' }}
        >
          {metrics.totalRecordsCount} Coaching
        </TitleContainer>
      </HeaderContainer>
      <TransitionalBarChart metricsData={metricsData} />
    </TaskMetricContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    employeeSummaryReportFilter:
      state.ReportReducer.employeeSummaryReportFilter,
    selectedDelegate: state.EmployeeReducer.selectedDelegatedEmployee,
    metrics: state.TaskReducer.coachingMetrics
  };
};
export default connect(mapReduxStateToProps)(EmpCoachingMetricChart);
